import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  appLogout,
  persistReducer,
  injectSlice,
} from '@bonanza-social/toolkit/state';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import LevelsService from '../service/LevelsService';
import { LoyaltyPointsUpdatedEventData, LoyaltyState } from '../Levels.types';

export const loyaltyPointsUpdated = createAction<LoyaltyPointsUpdatedEventData>(
  'levels/loyaltyPointsUpdated',
);

export const fetchLoyaltyLevel = createAsyncThunk(
  'levels/fetchCurrent',
  async () => {
    const res = await LevelsService.getLevels({
      params: {
        pageSize: 1,
      },
    });

    const [currentLevel] = res.items;
    const currentLevelPoints = {
      level: currentLevel.level,
      startPoints: currentLevel.startPoints,
      endPoints: currentLevel.endPoints,
    };

    return {
      currentLevel: currentLevelPoints,
      points: res.totalPoints,
    };
  },
);

const initialState: LoyaltyState = {
  data: [],
  dataState: 'idle',
  points: 0,
  currentLevelDataState: 'idle',
};

const loyalty = createSlice({
  name: 'loyalty',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loyaltyPointsUpdated, (state, action) => {
      state.points = action.payload.totalPoints;
      state.currentLevel = {
        level: action.payload.currentLevel,
        startPoints: action.payload.startPoints,
        endPoints: action.payload.endPoints,
      };
    });
    builder.addCase(fetchLoyaltyLevel.pending, (state) => {
      if (state.currentLevelDataState === 'fulfilled') {
        state.currentLevelDataState = 'refreshing';
      } else {
        state.currentLevelDataState = 'pending';
      }
    });
    builder.addCase(fetchLoyaltyLevel.rejected, (state) => {
      state.currentLevelDataState = 'rejected';
    });
    builder.addCase(fetchLoyaltyLevel.fulfilled, (state, action) => {
      state.points = action.payload.points;
      state.currentLevel = action.payload.currentLevel;

      state.currentLevelDataState = 'fulfilled';
    });
    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

const persistConfig = {
  key: 'loyalty',
  storage: AsyncStorage,
  whitelist: ['data', 'points', 'currentLevel'],
};

const persistedReducer = persistReducer(persistConfig, loyalty.reducer);

export const withLevels = injectSlice({
  reducerPath: 'loyalty',
  reducer: persistedReducer,
});

export const selectLevels = withLevels.selector((state) => state.loyalty.data);

export const selectLevelsDataState = withLevels.selector(
  (state) => state.loyalty.dataState,
);

export const selectPoints = withLevels.selector(
  (state) => state.loyalty.points,
);

export const selectLoyaltyLevel = withLevels.selector(
  (state) => state.loyalty.currentLevel,
);

export const selectLoyaltyLevelDataState = withLevels.selector(
  (state) => state.loyalty.currentLevelDataState,
);

export const selectLoyaltyLevelProgress = createSelector(
  selectLoyaltyLevel,
  selectPoints,
  (level, points) => {
    if (!level) {
      return 0;
    }
    return (
      ((points - level.startPoints) / (level.endPoints - level.startPoints)) *
      100
    );
  },
);

declare global {
  interface LazyLoadedSlices {
    loyalty: ReturnType<typeof persistedReducer>;
  }
}
